import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, FileText } from 'lucide-react'
import { StatusPill } from '@/components/status-pill'
import { Button } from '@/components/ui/button'
import { useQuoteBuilderApi } from '../api/client'
import { quoteCommentsQueryOptions, type QuoteComment } from '../publishing/api'
import { ErrorState, LoadingState, PageHeader, PageState, SurfaceCard } from '../ui/page'
import { groupComments, rankGroups, type CommentGroup } from './api'
import { AnswerComposer } from './answer-composer'
import { CommentMessage } from './comment-message'

/** "2: Fryer" → number "2" and name "Fryer"; options carry no number. */
function itemTitle(label: string | null) {
  if (label === null) return { number: null, name: 'Whole quote' }
  const match = /^(\d[\w.-]{0,7}): (.+)$/s.exec(label)
  return match ? { number: match[1]!, name: match[2]! } : { number: null, name: label }
}

function CommentThread({ quoteId, group }: { quoteId: string; group: CommentGroup }) {
  const { number, name } = itemTitle(group.label)
  const customerName = [...group.comments].reverse().find((comment) => comment.actor_id === null)?.author_name ?? null
  const headingId = `quote-comments-${group.key || 'quote'}`
  return (
    <SurfaceCard aria-labelledby={headingId}>
      <div className="flex items-start gap-3 border-b px-4 py-3">
        <span
          aria-hidden="true"
          className="grid size-6 shrink-0 place-items-center rounded-md bg-muted text-xs font-semibold tabular-nums text-muted-foreground"
        >
          {number ?? <FileText className="size-3.5" />}
        </span>
        <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5 sm:flex-row sm:justify-between sm:gap-3">
          <h2 className="min-w-0 text-sm font-semibold leading-6" id={headingId}>
            {number ? <span className="sr-only">Item {number}: </span> : null}
            {name}
          </h2>
          {group.waiting ? (
            <StatusPill className="sm:mt-0.5" tone="warning">
              Needs an answer
            </StatusPill>
          ) : null}
        </div>
      </div>
      <ol className="py-2">
        {group.comments.map((comment) => (
          <li key={comment.id}>
            <CommentMessage comment={comment} />
          </li>
        ))}
      </ol>
      <AnswerComposer customerName={customerName} itemName={name} quoteId={quoteId} rowRef={group.rowRef} />
    </SurfaceCard>
  )
}

/**
 * Items in the order they had when the page opened, so an answered item stays where the salesperson
 * is working; items the customer starts commenting on meanwhile come first.
 */
function CommentThreads({ quoteId, comments }: { quoteId: string; comments: QuoteComment[] }) {
  const groups = rankGroups(groupComments(comments))
  const [order] = useState(() => groups.map((group) => group.key))
  const ordered = [...groups].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
  return (
    <div className="space-y-4">
      {ordered.map((group) => (
        <CommentThread group={group} key={group.key} quoteId={quoteId} />
      ))}
    </div>
  )
}

/** The customer's comments on a published quote, by item, with Sales's answers (USA-1345). */
export function QuoteCommentsPage({ quoteId }: { quoteId: string }) {
  const api = useQuoteBuilderApi()
  const comments = useQuery({ ...quoteCommentsQueryOptions(api, quoteId), refetchInterval: 60_000 })
  return (
    <div className="mx-auto w-full max-w-4xl">
      <PageHeader
        actions={
          <Button asChild size="sm" variant="outline">
            <Link params={{ quoteId, stage: 'equipment' }} to="/quotes/$quoteId/workflow/$stage">
              <ArrowLeft aria-hidden="true" />
              Back to quote
            </Link>
          </Button>
        }
        title="Customer comments"
      />
      {comments.isLoading ? (
        <LoadingState label="Loading comments" />
      ) : !comments.data ? (
        <ErrorState
          description="Reload the page to try again."
          onRetry={() => void comments.refetch()}
          title="Comments unavailable"
        />
      ) : comments.data.length === 0 ? (
        <SurfaceCard>
          <PageState compact kind="empty" title="No comments yet" />
        </SurfaceCard>
      ) : (
        <CommentThreads comments={comments.data} key={quoteId} quoteId={quoteId} />
      )}
    </div>
  )
}
